import { Inject, Injectable } from '@angular/core';
import {
    ConcurrentStorageData,
    ConcurrentUsageService,
    CONFIRMATION_CONCURRENT_STORE_KEY,
    CONFIRMATION_TYPES,
    ConfirmationModel,
    ConfirmationModelFromJson,
    NotificationDataInstanceToken,
    NotificationDataInterface,
    NotificationIdentifierModel,
    NotificationIdentifierModelFromJson,
    TimesheetModel,
} from '@evorait/pace-core';
import { catchError, forkJoin, map, mergeMap, Observable, of, throwError } from 'rxjs';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

enum ConfirmationDataClassification {
    VALID = 'valid',
    STALE = 'stale',
    UNKNOWN = 'unknown',
}

@Injectable({ providedIn: 'root' })
export class StorageConfirmationService {
    public constructor(
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(NotificationDataInstanceToken) private readonly notificationService: NotificationDataInterface,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    public saveConfirmation(confirmation: ConfirmationModel): Observable<ConfirmationModel> {
        const confirmationKey = StorageConfirmationService.generateKey(
            confirmation.notificationIdentifier,
            confirmation.type,
        );
        this.log.debug(`${this.constructor.name}: saveConfirmation: saving confirmation`, {
            confirmation: confirmation.notificationIdentifier.toString(),
        });
        return this.concurrentUsageService
            .writeDataForKey(confirmationKey, JSON.stringify(confirmation), confirmation.saveTimestamp)
            .pipe(
                map(() => confirmation),
                catchError((error) => {
                    const errorMessage = `[ConfirmationStoreService](saveConfirmation): Confirmation with following key could not be saved to database: ${confirmation.notificationIdentifier.toString()}. Error ${error}`;
                    this.log.error(errorMessage);
                    return throwError(() => new Error(errorMessage));
                }),
            );
    }

    public removeConfirmation(confirmation: ConfirmationModel): Observable<boolean> {
        const confirmationKey = StorageConfirmationService.generateKey(
            confirmation.notificationIdentifier,
            confirmation.type,
        );
        this.log.debug(`${this.constructor.name}: removeConfirmation`, {
            confirmationKey,
        });
        return this.concurrentUsageService.deleteDataForKey(confirmationKey).pipe(
            map(() => true),
            catchError((error) => {
                const errorMessage = `[ConfirmationStoreService](removeConfirmation): Confirmation with following key could not be deleted from database: ${confirmation.notificationIdentifier.toString()}. Error ${error}`;
                this.log.error(errorMessage);
                return throwError(() => new Error(errorMessage));
            }),
        );
    }

    public getConfirmation(
        id: NotificationIdentifierModel,
        type: CONFIRMATION_TYPES,
        timesheet: TimesheetModel,
    ): Observable<ConfirmationModel> {
        const confirmationKey = StorageConfirmationService.generateKey(id, type);
        this.log.debug(
            `ConfirmationStoreService: getConfirmation: loading confirmation "${id}" by choosing local or remote copy.`,
        );
        return this.concurrentUsageService.readDataForKey(confirmationKey).pipe(
            mergeMap((res) => {
                if (!(res?.length > 0)) {
                    throw new Error(
                        `ConfirmationStoreService: getConfirmation: No confirmation for notification ${id.toString()} from user profile set`,
                    );
                }
                const data = JSON.parse(res[0].value);
                const resultConfirmation = new ConfirmationModelFromJson(data, type, timesheet);
                if (res[0].timestamp !== resultConfirmation.saveTimestamp) {
                    resultConfirmation.saveTimestamp = res[0].timestamp;
                }
                return of(resultConfirmation);
            }),
            catchError((error) => {
                this.log.debug(
                    `ConfirmationStoreService: getConfirmation: no confirmation from user profile set ${JSON.stringify(
                        error,
                    )}`,
                );
                return throwError(() => error);
            }),
        );
    }

    /**
     * Proactively purges CONFIRMATION-* entries whose stored notification identifier is no longer present in the
     * freshly loaded NdpHeaderSet list. Intended to run once right after notifications are (re-)loaded, so that
     * wrong/outdated SYNC data is cleaned up before it is ever read, rather than only when a confirmation is opened.
     */
    public purgeInvalidConfirmations(): Observable<void> {
        // Deleting here also queues a permanent remote deletion on the next upload (deleteDataForKey only
        // soft-marks locally, but that queue is what uploads deletions to UserProfileSet). A degraded or
        // partial NdpHeaderSet load must never be allowed to look like "every notification is gone" and
        // wipe every local confirmation as a result, so we only sweep once notifications are both loaded
        // and non-empty.
        if (!this.notificationService.isInitialized || this.notificationService.getAllNotifications().length === 0) {
            return of(undefined);
        }
        return this.concurrentUsageService.getAllPresentEntries().pipe(
            map((entries) =>
                entries
                    .filter((entry) => entry.key.startsWith(`${CONFIRMATION_CONCURRENT_STORE_KEY}-`))
                    .filter((entry) => this.classifyConfirmationEntry(entry) === ConfirmationDataClassification.STALE)
                    .map((entry) => entry.key),
            ),
            mergeMap((staleKeys) => {
                if (staleKeys.length === 0) {
                    return of(undefined);
                }
                this.log.debug(
                    `ConfirmationStoreService: purgeInvalidConfirmations: purging ${staleKeys.length} stale confirmation(s): ${staleKeys.join(', ')}`,
                );
                return forkJoin(staleKeys.map((key) => this.concurrentUsageService.deleteDataForKey(key)));
            }),
            map(() => undefined),
            catchError((error) => {
                this.log.error(
                    `ConfirmationStoreService: purgeInvalidConfirmations: failed to purge stale confirmations: ${error}`,
                );
                return of(undefined);
            }),
        );
    }

    // Deletion must only ever be triggered by a positive identification of staleness (a parseable
    // confirmation whose notification is confirmed absent). Anything we can't positively parse - a
    // different kind of entry sharing the key prefix, a corrupted payload, etc. - is left alone rather
    // than purged, since purging is a destructive, remotely-synced operation.
    private classifyConfirmationEntry(entry: ConcurrentStorageData): ConfirmationDataClassification {
        try {
            const data = JSON.parse(entry.value);
            const storedIdentifier = new NotificationIdentifierModelFromJson(data.notificationIdentifier);
            return this.notificationService.getNotificationByIdentifier(storedIdentifier)
                ? ConfirmationDataClassification.VALID
                : ConfirmationDataClassification.STALE;
        } catch (error) {
            this.log.error(
                `ConfirmationStoreService: purgeInvalidConfirmations: could not parse entry "${entry.key}" as a confirmation, leaving it untouched: ${error}`,
            );
            return ConfirmationDataClassification.UNKNOWN;
        }
    }

    private static generateKey(id: NotificationIdentifierModel, type: CONFIRMATION_TYPES): string {
        return `${CONFIRMATION_CONCURRENT_STORE_KEY}-${id.toString()}-${type}`;
    }
}
